import { PersonAgeStatus } from '@core/api/generated/api-client';
import { MemberAttendanceDto } from '../models/divine-service.model';

/** Valores de PersonAgeStatus que corresponden a menores */
const MINOR_STATUSES = new Set<string>([
  'Baby', 'PED1', 'PED2', 'ED1', 'ED2', 'ED3', 'ED4', 'ER1', 'ER2', 'ER3', 'AC'
]);
const MINOR_STATUS_VALUES = new Set<number>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);

/**
 * Helper utility para filtrar y operar sobre miembros basándose en categoría de edad
 */
export class MemberFilterHelper {
  static isMinor(member: MemberAttendanceDto): boolean {
    const ageStatus = member.ageStatus as string | number | null | undefined;
    return typeof ageStatus === 'number'
      ? MINOR_STATUS_VALUES.has(ageStatus)
      : ageStatus != null && MINOR_STATUSES.has(ageStatus);
  }

  /**
   * Filtra miembros que son adultos (no menores)
   * @param members - Array de miembros a filtrar
   * @param onlyAttended - Si es true, solo devuelve adultos que asistieron
   */
  static filterAdults(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return members.filter(m => {
      if (this.isNominalVisitor(m)) return false;
      const isAdult = !this.isMinor(m);
      return onlyAttended ? isAdult && m.attended : isAdult;
    });
  }

  /**
   * Filtra miembros que son menores
   * @param members - Array de miembros a filtrar
   * @param onlyAttended - Si es true, solo devuelve menores que asistieron
   */
  static filterMinors(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return members.filter(m => {
      if (this.isNominalVisitor(m)) return false;
      const isMinor = this.isMinor(m);
      return onlyAttended ? isMinor && m.attended : isMinor;
    });
  }

  static filterSealed(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return members.filter(m => {
      if (this.isNominalVisitor(m)) return false;
      const isSealed = m.isSealed === true;
      return onlyAttended ? isSealed && m.attended : isSealed;
    });
  }

  static filterUnsealed(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return members.filter(m => {
      if (this.isNominalVisitor(m)) return false;
      const isUnsealed = m.isSealed !== true;
      return onlyAttended ? isUnsealed && m.attended : isUnsealed;
    });
  }

  static filterSealedAdults(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return this.filterAdults(this.filterSealed(members, onlyAttended));
  }

  static filterSealedMinors(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return this.filterMinors(this.filterSealed(members, onlyAttended));
  }

  static filterUnsealedAdults(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return this.filterAdults(this.filterUnsealed(members, onlyAttended));
  }

  static filterUnsealedMinors(members: MemberAttendanceDto[], onlyAttended = false): MemberAttendanceDto[] {
    return this.filterMinors(this.filterUnsealed(members, onlyAttended));
  }

  /**
   * Calcula los totales de adultos y menores en una lista de miembros
   * @param members - Array de miembros
   * @param countOnlyAttended - Si es true, cuenta solo los que asistieron
   */
  static getAgeGroupCounts(
    members: MemberAttendanceDto[],
    countOnlyAttended = false
  ): { adults: number; minors: number } {
    return {
      adults: this.filterAdults(members, countOnlyAttended).length,
      minors: this.filterMinors(members, countOnlyAttended).length
    };
  }

  static getSealingAgeCounts(
    members: MemberAttendanceDto[],
    countOnlyAttended = false
  ): { sealedAdults: number; sealedMinors: number; unsealedAdults: number; unsealedMinors: number } {
    return {
      sealedAdults: this.filterSealedAdults(members, countOnlyAttended).length,
      sealedMinors: this.filterSealedMinors(members, countOnlyAttended).length,
      unsealedAdults: this.filterUnsealedAdults(members, countOnlyAttended).length,
      unsealedMinors: this.filterUnsealedMinors(members, countOnlyAttended).length
    };
  }

  /**
   * Obtiene el total de miembros de una categoría de edad
   * @param members - Array de miembros
   * @param ageStatus - PersonAgeStatus a contar
   * @param countOnlyAttended - Si es true, cuenta solo los que asistieron
   */
  static countByAgeStatus(
    members: MemberAttendanceDto[],
    ageStatus: PersonAgeStatus | number,
    countOnlyAttended = false
  ): number {
    return members.filter(m => {
      const matches = m.ageStatus === ageStatus;
      return countOnlyAttended ? matches && m.attended : matches;
    }).length;
  }

  /**
   * Filtra miembros por una lista de IDs
   * @param members - Array de miembros
   * @param memberIds - Array de IDs a filtrar
   */
  static filterByIds(members: MemberAttendanceDto[], memberIds: string[]): MemberAttendanceDto[] {
    return members.filter(m => memberIds.includes(m.personId));
  }

  static getNominalVisitorAgeCounts(members: MemberAttendanceDto[]): { adults: number; minors: number } {
    const visitors = members.filter(m => this.isNominalVisitor(m));
    return {
      adults: visitors.filter(m => !this.isMinor(m)).length,
      minors: visitors.filter(m => this.isMinor(m)).length
    };
  }

  private static isNominalVisitor(member: MemberAttendanceDto): boolean {
    return member.attended && (member.isVisitor === true || member.attendanceCategory === 'Visitor');
  }

  /**
   * Obtiene estadísticas completas de asistencia
   */
  static getAttendanceStats(members: MemberAttendanceDto[]): {
    totalAdults: number;
    totalMinors: number;
    attendedAdults: number;
    attendedMinors: number;
    totalAttended: number;
    totalMembers: number;
    attendancePercentage: number;
  } {
    const totalAdults = this.filterAdults(members).length;
    const totalMinors = this.filterMinors(members).length;
    const attendedAdults = this.filterAdults(members, true).length;
    const attendedMinors = this.filterMinors(members, true).length;
    const totalAttended = attendedAdults + attendedMinors;
    const totalMembers = members.length;
    const attendancePercentage = totalMembers > 0 ? (totalAttended / totalMembers) * 100 : 0;

    return {
      totalAdults,
      totalMinors,
      attendedAdults,
      attendedMinors,
      totalAttended,
      totalMembers,
      attendancePercentage
    };
  }
}
